// InvoiceProfileService.tsx
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { InvoiceProfileService } from '../../core/services';
import { PageSize } from '../../type';
import {
  GetDeliveryNoteByIdResponse,
  GetDeliveryNotesResponse,
  GetInvoiceByIdResponse,
  GetInvoiceTemplateByIdResponse,
  GetReviewByIdResponse,
} from './interface';

export const useGetInvoiceService = (initialParams: PageSize) => {
  const { data, isLoading, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching } =
    useInfiniteQuery({
      queryKey: ['get-invoices-data'],
      queryFn: async ({ pageParam }) => {
        return InvoiceProfileService.getInvoice({
          page: initialParams.pageNumber ?? pageParam,
          size: initialParams.size,
          invoiceNo: initialParams.invoiceNo,
          stationId: initialParams.stationId,
          vehiclePlate: initialParams.vehiclePlate,
          vehicleVin: initialParams.vehicleVin,
          type: initialParams.type,
          status: initialParams.status,
          clientName: initialParams.clientName,
          start: initialParams.start,
          end: initialParams.end,
          accountId: initialParams.accountId,
          userId: initialParams.userId,
          invoiceType: initialParams.invoiceType,
          vehicleId: initialParams.vehicleId,
        });
      },
      initialPageParam: 1,
      getNextPageParam: (res) => (!res?.last ? res?.number + 1 : undefined),
      enabled: false,
      retry: 1,
      retryDelay: 36000000,
    });
  const isDataFetching = isFetching || isFetchingNextPage;

  return {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isDataFetching,
  };
};

export const useGetCreditNoteService = (initialParams: PageSize) => {
  const { data, isLoading, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching } =
    useInfiniteQuery({
      queryKey: ['get-creditnotes-data'],
      queryFn: async ({ pageParam }) => {
        return InvoiceProfileService.getCreditNotes({
          page: initialParams.pageNumber ?? pageParam,
          size: initialParams.size,
          stationId: initialParams.stationId,
          vehiclePlate: initialParams.vehiclePlate,
          vehicleVin: initialParams.vehicleVin,
          clientName: initialParams.clientName,
          start: initialParams.start,
          end: initialParams.end,
          creditNoteNo: initialParams.creditNoteNo,
        });
      },
      initialPageParam: 1,
      getNextPageParam: (res) => (!res?.last ? res?.number + 1 : undefined),
      enabled: false,
      retry: 1,
      retryDelay: 36000000,
    });
  const isDataFetching = isFetching || isFetchingNextPage;

  return {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isDataFetching,
  };
};

export const useGetInvoiceTemplateService = (initialParams: PageSize) => {
  const { data, isLoading, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching } =
    useInfiniteQuery({
      queryKey: ['get-invoice-template-dropdown-data'],
      queryFn: async ({ pageParam }) => {
        return InvoiceProfileService.getInvoiceTemplatesDropdown({
          page: initialParams.pageNumber ?? pageParam,
          size: initialParams.size,
          type: initialParams.type,
        });
      },
      initialPageParam: 1,
      getNextPageParam: (res) => {
        return !res?.last ? res?.number + 1 : undefined;
      },
      enabled: false,
    });
  const isDataFetching = isFetching || isFetchingNextPage;

  return {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isDataFetching,
  };
};

export const useGetInvoiceByIDService = (id: number) => {
  const { data, isLoading, isRefetching, error, refetch } = useQuery<GetInvoiceByIdResponse, unknown>({
    queryKey: ['get-invoice-by-id-data'],
    queryFn: async () => {
      return InvoiceProfileService.getInvoiceByID(id);
    },
    enabled: false,
  });

  return {
    data,
    isLoading,
    isRefetching,
    error,
    refetch,
  };
};

export const useGetDeliveryNoteByIDService = (id: number) => {
  const { data, isLoading, isRefetching, error, refetch } = useQuery<GetDeliveryNoteByIdResponse, unknown>({
    queryKey: ['get-delivery-note-by-id-data'],
    queryFn: async () => {
      return InvoiceProfileService.getDeliveryNoteByID(id);
    },
    enabled: false,
  });

  return {
    data,
    isLoading,
    isRefetching,
    error,
    refetch,
  };
};

export const useGetDeliveryNotesService = (initialParams: PageSize) => {
  const { data, isLoading, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching } =
    useInfiniteQuery({
      queryKey: ['get-delivery-notes-data'],
      queryFn: async ({ pageParam }) => {
        return InvoiceProfileService.getDeliveryNotes({
          page: initialParams.pageNumber ?? pageParam,
          size: initialParams.size,
          deliveryNoteNo: initialParams.deliveryNoteNo,
          stationId: initialParams.stationId,
          vehiclePlate: initialParams.vehiclePlate,
          vehicleVin: initialParams.vehicleVin,
          invoiceStatus: initialParams.invoiceStatus,
          clientName: initialParams.clientName,
          start: initialParams.start,
          end: initialParams.end,
          accountId: initialParams.accountId,
          userId: initialParams.userId,
        });
      },
      initialPageParam: 1,
      getNextPageParam: (res: GetDeliveryNotesResponse) => (!res?.last ? res?.number + 1 : undefined),
      enabled: false,
      retry: 1,
      retryDelay: 36000000,
    });
  const isDataFetching = isFetching || isFetchingNextPage;

  return {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isDataFetching,
  };
};

export const useGetInvoiceTemplateByIDService = (id: number) => {
  const { data, isLoading, isRefetching, error, refetch } = useQuery<GetInvoiceTemplateByIdResponse, unknown>({
    queryKey: ['invoice-template-by-id-data'],
    queryFn: async () => {
      return InvoiceProfileService.getInvoiceTemplateByID(id);
    },
    enabled: false,
  });

  return {
    data,
    isLoading,
    isRefetching,
    error,
    refetch,
  };
};

export const useGetReviewByIdService = (id: number | undefined) => {
  const { data, isLoading, error, refetch } = useQuery<GetReviewByIdResponse, unknown>({
    queryKey: ['get-review-by-id-data'],
    queryFn: async () => {
      return InvoiceProfileService.getReviewByID(id);
    },
    enabled: false,
  });

  return {
    data,
    isLoading,
    error,
    refetch,
  };
};
